/**
 * Entity pages are extensible (IA §3): modules contribute tabs to /vehicles/:id,
 * /drivers/:id, /devices/:id … without editing the owning module.
 *
 *   registerEntityTab('vehicle', {
 *     key: 'video', label: msg('Video'), permission: 'media.video.read', module: 'media',
 *     order: 60, component: lazy(() => import('./tabs/VehicleVideoTab')),
 *   });
 */
import type { ComponentType, LazyExoticComponent } from 'react';
import { can, type PermissionCheck } from '@/lib/can';
import { moduleEnabled, useMe } from '@/lib/me';

export type EntityType = 'vehicle' | 'driver' | 'device' | 'asset' | 'trip' | 'place' | (string & {});

export interface EntityTabProps {
  /** The entity id from the URL. Tabs load what they need themselves. */
  id: string;
}

export interface EntityTabDef {
  key: string;
  /** English source string, marked with msg(). */
  label: string;
  permission?: PermissionCheck;
  module?: string;
  order: number;
  component: ComponentType<EntityTabProps> | LazyExoticComponent<ComponentType<EntityTabProps>>;
}

const registry = new Map<string, EntityTabDef[]>();

export function registerEntityTab(entity: EntityType, def: EntityTabDef): void {
  const list = (registry.get(entity) ?? []).filter((d) => d.key !== def.key);
  list.push(def);
  list.sort((a, b) => a.order - b.order);
  registry.set(entity, list);
}

export function entityTabs(entity: EntityType): EntityTabDef[] {
  return registry.get(entity) ?? [];
}

/** The tabs this user may see on this entity page, in order. */
export function useEntityTabs(entity: EntityType): EntityTabDef[] {
  const me = useMe();
  return entityTabs(entity).filter((d) => can(me, d.permission) && moduleEnabled(me, d.module));
}
